// =============================================================================
// AGC: DROID FARM - Field Monitoring Service
// AGC: Handles Firestore persistence + localStorage caching for field observations
// AGC: and stress events from Sentinel-2 NDVI analysis
// =============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { db } from './firebase-auth'
import { getBackendUrl } from '../utils/backendUrl'
import type {
  FieldObservation,
  StressEvent,
  FieldHealthResult,
  TrendAnalysisResult,
  ManagementZone,
  ZoneClassificationResult,
  CacheEntry,
  MonitoringCache,
  AlertSummary,
  StressEventStatus,
} from '../types'

// =============================================================================
// AGC: Cache Configuration
// =============================================================================

// AGC: Cache TTL values in milliseconds
const CACHE_TTL = {
  health: 6 * 60 * 60 * 1000, // AGC: 6 hours for health data
  observations: 6 * 60 * 60 * 1000, // AGC: 6 hours for observation time series
  stressEvents: 30 * 60 * 1000, // AGC: 30 minutes for stress events
  zones: 24 * 60 * 60 * 1000, // AGC: 24 hours for zone classification
  trend: 6 * 60 * 60 * 1000, // AGC: 6 hours for trend analysis
  overlay: 30 * 24 * 60 * 60 * 1000, // AGC: 30 days for NDVI overlay PNGs
}

// AGC: Sentinel Hub error circuit-breaker
// AGC: Once we get a 403/expired-account error, stop retrying for 30 minutes
// AGC: This prevents console flooding when the account is expired
let sentinelHubBlocked = false
let sentinelHubBlockedUntil = 0
const SENTINEL_BLOCK_DURATION = 30 * 60 * 1000 // AGC: 30 minutes

function blockSentinelHub(reason: string) {
  if (!sentinelHubBlocked) {
    console.warn(`⚠️ AGC: Sentinel Hub blocked for 30 min: ${reason}`)
  }
  sentinelHubBlocked = true
  sentinelHubBlockedUntil = Date.now() + SENTINEL_BLOCK_DURATION
}

function isSentinelHubBlocked(): boolean {
  if (!sentinelHubBlocked) return false
  if (Date.now() > sentinelHubBlockedUntil) {
    sentinelHubBlocked = false
    return false
  }
  return true
}

// AGC: LocalStorage keys prefix
const STORAGE_PREFIX = 'droidfarm_monitoring_'

// AGC: Backend URL
const BASE_URL = getBackendUrl()

// =============================================================================
// AGC: LocalStorage Cache Helpers
// =============================================================================

/**
 * AGC: Get cached data from localStorage
 */
function getFromCache<T>(key: string): CacheEntry<T> | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${key}`)
    if (!stored) return null

    const entry: CacheEntry<T> = JSON.parse(stored)
    
    // AGC: Check if cache is expired
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(`${STORAGE_PREFIX}${key}`)
      return null
    }

    return entry
  } catch (error) {
    console.error('AGC: Error reading from cache:', error)
    return null
  }
}

/**
 * AGC: Store data in localStorage cache
 */
function setInCache<T>(key: string, data: T, ttl: number): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    }
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(entry))
  } catch (error) {
    console.error('AGC: Error writing to cache:', error)
    // AGC: If localStorage is full, try to clear old monitoring data
    clearOldCache()
  }
}

/**
 * AGC: Clear cache entry
 */
function clearCache(key: string): void {
  localStorage.removeItem(`${STORAGE_PREFIX}${key}`)
}

/**
 * AGC: Clear all monitoring cache
 */
export function clearAllMonitoringCache(): void {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith(STORAGE_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key))
  console.log(`AGC: Cleared ${keysToRemove.length} monitoring cache entries`)
}

/**
 * AGC: Clear old/expired cache entries
 */
function clearOldCache(): void {
  const now = Date.now()
  const keysToRemove: string[] = []
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith(STORAGE_PREFIX)) {
      try {
        const stored = localStorage.getItem(key)
        if (stored) {
          const entry = JSON.parse(stored)
          if (entry.expiresAt && now > entry.expiresAt) {
            keysToRemove.push(key)
          }
        }
      } catch {
        keysToRemove.push(key!)
      }
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key))
}

// =============================================================================
// AGC: Firestore Collections
// =============================================================================

const COLLECTIONS = {
  observations: 'field_observations',
  events: 'field_events',
}

// =============================================================================
// AGC: Field Observations (Firestore + Cache)
// =============================================================================

/**
 * AGC: Get field observations from cache or Firestore
 */
export async function getFieldObservations(
  fieldId: string,
  options: { from?: string; to?: string; forceRefresh?: boolean } = {}
): Promise<FieldObservation[]> {
  const cacheKey = `observations_${fieldId}`
  
  // AGC: Check cache first (unless force refresh)
  if (!options.forceRefresh) {
    const cached = getFromCache<FieldObservation[]>(cacheKey)
    if (cached) {
      console.log('AGC: Using cached observations for field:', fieldId)
      return filterObservationsByDate(cached.data, options.from, options.to)
    }
  }

  try {
    // AGC: Fetch from Firestore
    const obsRef = collection(db, COLLECTIONS.observations)
    const q = query(
      obsRef,
      where('fieldId', '==', fieldId),
      orderBy('date', 'desc'),
      limit(100) // AGC: Last 100 observations
    )
    
    const snapshot = await getDocs(q)
    const observations: FieldObservation[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as FieldObservation))

    // AGC: Cache the results
    setInCache(cacheKey, observations, CACHE_TTL.observations)
    console.log(`AGC: Cached ${observations.length} observations for field:`, fieldId)

    return filterObservationsByDate(observations, options.from, options.to)
  } catch (error) {
    console.error('AGC: Error fetching observations:', error)
    return []
  }
}

/**
 * AGC: Filter observations by date range
 */
function filterObservationsByDate(
  observations: FieldObservation[],
  from?: string,
  to?: string
): FieldObservation[] {
  return observations.filter(obs => {
    if (from && obs.date < from) return false
    if (to && obs.date > to) return false
    return true
  })
}

/**
 * AGC: Save a new field observation to Firestore
 */
export async function saveFieldObservation(
  observation: Omit<FieldObservation, 'id' | 'createdAt' | 'updatedAt'>,
  userId?: string
): Promise<string> {
  try {
    const obsRef = collection(db, COLLECTIONS.observations)
    const docId = `${observation.fieldId}_${observation.date}`
    const docRef = doc(obsRef, docId)
    
    await setDoc(docRef, {
      ...observation,
      userId: userId || observation.userId, // AGC: Include userId for Firestore rules
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    // AGC: Invalidate cache
    clearCache(`observations_${observation.fieldId}`)

    console.log('AGC: Saved observation for field:', observation.fieldId, 'date:', observation.date)
    return docId
  } catch (error) {
    console.error('AGC: Error saving observation:', error)
    throw error
  }
}

/**
 * AGC: Batch save multiple observations
 */
export async function saveFieldObservationsBatch(
  observations: Omit<FieldObservation, 'id' | 'createdAt' | 'updatedAt'>[]
): Promise<void> {
  for (const observation of observations) {
    await saveFieldObservation(observation)
  }
}

// =============================================================================
// AGC: Stress Events (Firestore + Cache)
// =============================================================================

/**
 * AGC: Get stress events for a field
 */
export async function getStressEvents(
  fieldId: string,
  options: { status?: StressEventStatus; forceRefresh?: boolean } = {}
): Promise<StressEvent[]> {
  const cacheKey = `events_${fieldId}`
  
  // AGC: Check cache first
  if (!options.forceRefresh) {
    const cached = getFromCache<StressEvent[]>(cacheKey)
    if (cached) {
      console.log('AGC: Using cached stress events for field:', fieldId)
      if (options.status) {
        return cached.data.filter(e => e.status === options.status)
      }
      return cached.data
    }
  }

  try {
    const eventsRef = collection(db, COLLECTIONS.events)
    const q = query(
      eventsRef,
      where('fieldId', '==', fieldId),
      orderBy('date', 'desc'),
      limit(50)
    )
    
    const snapshot = await getDocs(q)
    const events: StressEvent[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as StressEvent))

    // AGC: Cache results
    setInCache(cacheKey, events, CACHE_TTL.stressEvents)
    console.log(`AGC: Cached ${events.length} stress events for field:`, fieldId)

    if (options.status) {
      return events.filter(e => e.status === options.status)
    }
    return events
  } catch (error) {
    console.error('AGC: Error fetching stress events:', error)
    return []
  }
}

/**
 * AGC: Create a new stress event
 */
export async function createStressEvent(
  event: Omit<StressEvent, 'id' | 'createdAt' | 'updatedAt'>,
  userId?: string
): Promise<string> {
  try {
    const eventsRef = collection(db, COLLECTIONS.events)
    const docId = `${event.fieldId}_${event.eventType}_${event.date}_${Date.now()}`
    const docRef = doc(eventsRef, docId)
    
    await setDoc(docRef, {
      ...event,
      userId: userId || event.userId, // AGC: Include userId for Firestore rules
      status: event.status || 'OPEN',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    // AGC: Invalidate cache
    clearCache(`events_${event.fieldId}`)

    console.log('AGC: Created stress event:', event.eventType, 'for field:', event.fieldId)
    return docId
  } catch (error) {
    console.error('AGC: Error creating stress event:', error)
    throw error
  }
}

/**
 * AGC: Update stress event status (e.g., mark as resolved)
 */
export async function updateStressEventStatus(
  eventId: string,
  fieldId: string,
  status: StressEventStatus,
  resolvedBy?: string
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTIONS.events, eventId)
    
    const updates: Record<string, unknown> = {
      status,
      updatedAt: serverTimestamp(),
    }
    
    if (status === 'RESOLVED' && resolvedBy) {
      updates.resolvedAt = new Date().toISOString()
      updates.resolvedBy = resolvedBy
    }
    
    await updateDoc(docRef, updates)

    // AGC: Invalidate cache
    clearCache(`events_${fieldId}`)

    console.log('AGC: Updated stress event status:', eventId, 'to', status)
  } catch (error) {
    console.error('AGC: Error updating stress event:', error)
    throw error
  }
}

// =============================================================================
// AGC: Backend API Calls
// =============================================================================

/**
 * AGC: Fetch field health from backend API
 */
export async function fetchFieldHealth(
  fieldId: string,
  bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } },
  fieldPolygon?: [number, number][],
  authToken?: string | null,
  options: { timeWindowDays?: number; forceRefresh?: boolean } = {}
): Promise<FieldHealthResult | null> {
  const cacheKey = `health_${fieldId}`
  
  // AGC: Check cache first
  if (!options.forceRefresh) {
    const cached = getFromCache<FieldHealthResult>(cacheKey)
    if (cached) {
      console.log('AGC: Using cached health for field:', fieldId)
      return cached.data
    }
  }

  // AGC: Circuit breaker - skip if Sentinel Hub is blocked (expired/forbidden)
  if (isSentinelHubBlocked()) {
    return null
  }

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    // AGC: Convert fieldPolygon from [lat, lng] to [lng, lat] format for backend
    const polygonForApi = fieldPolygon 
      ? fieldPolygon.map(([lat, lng]) => [lng, lat] as [number, number])
      : undefined

    console.log('AGC: Sending health request with polygon:', polygonForApi?.length, 'points')

    const response = await fetch(`${BASE_URL}/api/v1/boundaries/health`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        bounds,
        fieldPolygon: polygonForApi,
        timeWindowDays: options.timeWindowDays || 30,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMsg = JSON.stringify(errorData)
      // AGC: Detect expired/forbidden Sentinel Hub and block further requests
      if (errorMsg.includes('403') || errorMsg.includes('Forbidden') || errorMsg.includes('expired')) {
        blockSentinelHub(errorMsg)
      } else {
        console.error('AGC: Health API error:', response.status, errorMsg)
      }
      return null
    }

    const result = await response.json()
    if (result.success && result.data) {
      const healthResult: FieldHealthResult = result.data
      
      // AGC: Cache the result
      setInCache(cacheKey, healthResult, CACHE_TTL.health)
      console.log('AGC: Cached health for field:', fieldId, 'score:', healthResult.health?.score)

      // AGC: Also save observation to Firestore for historical tracking
      if (healthResult.health) {
        await saveFieldObservation({
          fieldId,
          date: new Date().toISOString().split('T')[0],
          validPixelRatio: healthResult.health.metrics.validDataRatio,
          cloudy: healthResult.health.metrics.validDataRatio < 0.6,
          ndvi: {
            mean: healthResult.health.metrics.meanNdvi,
            p10: 0, // AGC: Not available from current API
            p50: 0,
            p90: 0,
            stdev: healthResult.health.metrics.stdNdvi,
          },
          health: {
            score: healthResult.health.score,
            grade: healthResult.health.grade,
            gradeLabel: healthResult.health.gradeLabel,
            gradeColor: healthResult.health.gradeColor,
          },
          source: {
            provider: 'sentinel-hub',
            dataset: 'sentinel-2-l2a',
          },
        }).catch(err => console.warn('AGC: Could not save observation to Firestore:', err))
      }

      return healthResult
    }

    return null
  } catch (error) {
    console.error('AGC: Error fetching field health:', error)
    return null
  }
}

/**
 * AGC: Fetch NDVI trend analysis from backend
 */
export async function fetchTrendAnalysis(
  fieldId: string,
  bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } },
  authToken?: string | null,
  options: { startDate?: string; endDate?: string; interval?: number; forceRefresh?: boolean } = {}
): Promise<TrendAnalysisResult | null> {
  const cacheKey = `trend_${fieldId}`
  
  // AGC: Check cache first
  if (!options.forceRefresh) {
    const cached = getFromCache<TrendAnalysisResult>(cacheKey)
    if (cached) {
      console.log('AGC: Using cached trend for field:', fieldId)
      return cached.data
    }
  }

  // AGC: Circuit breaker - skip if Sentinel Hub is blocked (expired/forbidden)
  if (isSentinelHubBlocked()) {
    return null
  }

  try {
    // AGC: Default date range: last 90 days
    const endDate = options.endDate || new Date().toISOString().split('T')[0]
    const startDate = options.startDate || new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/boundaries/trend`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        bounds,
        startDate,
        endDate,
        interval: options.interval || 14,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMsg = JSON.stringify(errorData)
      // AGC: Detect expired/forbidden Sentinel Hub and block further requests
      if (errorMsg.includes('403') || errorMsg.includes('Forbidden') || errorMsg.includes('expired')) {
        blockSentinelHub(errorMsg)
      } else {
        console.error('AGC: Trend API error:', errorData)
      }
      return null
    }

    const result = await response.json()
    if (result.success && result.data) {
      // AGC: Cache the result
      setInCache(cacheKey, result.data, CACHE_TTL.trend)
      console.log('AGC: Cached trend for field:', fieldId)
      return result.data
    }

    return null
  } catch (error) {
    console.error('AGC: Error fetching trend analysis:', error)
    return null
  }
}

/**
 * AGC: Fetch management zones from backend
 */
export async function fetchManagementZones(
  fieldId: string,
  bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } },
  authToken?: string | null,
  options: { numZones?: number; forceRefresh?: boolean } = {}
): Promise<ZoneClassificationResult | null> {
  const cacheKey = `zones_${fieldId}`
  
  // AGC: Check cache first
  if (!options.forceRefresh) {
    const cached = getFromCache<ZoneClassificationResult>(cacheKey)
    if (cached) {
      console.log('AGC: Using cached zones for field:', fieldId)
      return cached.data
    }
  }

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/boundaries/zones`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        bounds,
        numZones: options.numZones || 3,
        timeWindowDays: 30,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('AGC: Zones API error:', errorData)
      return null
    }

    const result = await response.json()
    if (result.success && result.data) {
      // AGC: Transform backend zones to frontend format
      const backendZones = result.data.zones || []
      const geoJSONFeatures = result.data.geoJSON?.features || []
      
      const transformedZones = backendZones.map((zone: any, index: number) => {
        // AGC: Find matching GeoJSON feature for this zone
        const zoneId = zone.zoneId ?? index + 1
        const matchingFeature = geoJSONFeatures.find((f: any) => f.properties?.zoneId === zoneId)
        
        return {
          id: zoneId,
          label: zone.name || `Zone ${index + 1}`,
          color: zone.color || '#888888',
          percentage: zone.statistics?.percentageOfField ?? 0,
          meanNdvi: zone.statistics?.mean,
          ndviRange: zone.statistics ? {
            min: zone.statistics.min,
            max: zone.statistics.max,
          } : undefined,
          area: undefined, // AGC: Not provided by backend
          recommendation: zone.recommendations?.[0]?.action || zone.description,
          pixelCount: zone.statistics?.pixelCount,
          // AGC: Include GeoJSON geometry for map rendering
          geoJSON: matchingFeature?.geometry || null,
        }
      })

      const transformedData = {
        ...result.data,
        zones: transformedZones,
      }

      // AGC: Cache the transformed result
      setInCache(cacheKey, transformedData, CACHE_TTL.zones)
      console.log('AGC: Cached zones for field:', fieldId, 'zones:', transformedZones.length, 'with geoJSON:', geoJSONFeatures.length)
      return transformedData
    }

    return null
  } catch (error) {
    console.error('AGC: Error fetching management zones:', error)
    return null
  }
}

/**
 * AGC: Fetch NDVI overlay PNG and return a blob URL
 * AGC: Actually fetches the image from the backend to verify it works
 */
export async function fetchNdviOverlayUrl(
  fieldId: string,
  bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } },
  options: { date?: string; width?: number; height?: number; days?: number } = {}
): Promise<string | null> {
  // AGC: Circuit breaker check
  if (isSentinelHubBlocked()) {
    console.warn('AGC: Sentinel Hub blocked by circuit breaker, skipping NDVI overlay')
    return null
  }

  const dateKey = options.date || 'latest'
  const cacheKey = `overlay_${fieldId}_${dateKey}`

  // AGC: Check cache first
  const cached = getFromCache<string>(cacheKey)
  if (cached) {
    console.log('AGC: Using cached NDVI overlay for field:', fieldId)
    return cached.data
  }

  try {
    // AGC: Build the overlay URL with query params
    const params = new URLSearchParams({
      minLon: bounds.southWest.lng.toString(),
      minLat: bounds.southWest.lat.toString(),
      maxLon: bounds.northEast.lng.toString(),
      maxLat: bounds.northEast.lat.toString(),
      width: (options.width || 512).toString(),
      height: (options.height || 512).toString(),
      days: (options.days || 30).toString(),
    })

    const url = `${BASE_URL}/api/v1/boundaries/ndvi/overlay?${params.toString()}`

    // AGC: Actually fetch the image to verify the backend returns a valid PNG
    console.log('AGC: Fetching NDVI overlay from backend for field:', fieldId)
    const response = await fetch(url)

    if (!response.ok) {
      const errorText = await response.text().catch(() => `HTTP ${response.status}`)
      console.error(`AGC: NDVI overlay fetch failed (${response.status}):`, errorText)
      // AGC: Block further requests on auth errors
      if (response.status === 403 || response.status === 401) {
        blockSentinelHub(`NDVI overlay returned ${response.status}`)
      }
      return null
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('image/')) {
      console.error('AGC: NDVI overlay returned non-image content-type:', contentType)
      return null
    }

    // AGC: Create blob URL from the fetched image
    const blob = await response.blob()
    if (blob.size < 100) {
      console.warn('AGC: NDVI overlay too small, likely empty:', blob.size, 'bytes')
      return null
    }

    const blobUrl = URL.createObjectURL(blob)
    setInCache(cacheKey, blobUrl, CACHE_TTL.overlay)
    console.log('AGC: NDVI overlay loaded successfully for field:', fieldId, `(${blob.size} bytes)`)
    return blobUrl
  } catch (error) {
    console.error('AGC: Error fetching NDVI overlay:', error)
    return null
  }
}

// =============================================================================
// AGC: Alert Summary Helper
// =============================================================================

/**
 * AGC: Generate alert summary from health result and stress events
 */
export function generateAlertSummary(
  healthResult: FieldHealthResult | null,
  stressEvents: StressEvent[]
): AlertSummary {
  const openEvents = stressEvents.filter(e => e.status === 'OPEN')
  const alerts = healthResult?.alerts || []
  
  const bySeverity = {
    critical: [...alerts.filter(a => a.severity === 'critical'), ...openEvents.filter(e => e.severity === 'critical')].length,
    warning: [...alerts.filter(a => a.severity === 'warning'), ...openEvents.filter(e => e.severity === 'warning')].length,
    info: [...alerts.filter(a => a.severity === 'info'), ...openEvents.filter(e => e.severity === 'info')].length,
  }

  const byType: Record<string, number> = {}
  for (const alert of alerts) {
    byType[alert.type] = (byType[alert.type] || 0) + 1
  }
  for (const event of openEvents) {
    byType[event.eventType] = (byType[event.eventType] || 0) + 1
  }

  let status: 'critical' | 'warning' | 'info' | 'healthy'
  let statusMessage: string

  if (bySeverity.critical > 0) {
    status = 'critical'
    statusMessage = `${bySeverity.critical} critical issue(s) require immediate attention`
  } else if (bySeverity.warning > 0) {
    status = 'warning'
    statusMessage = `${bySeverity.warning} warning(s) should be investigated`
  } else if (bySeverity.info > 0) {
    status = 'info'
    statusMessage = `${bySeverity.info} informational notice(s)`
  } else {
    status = 'healthy'
    statusMessage = 'No alerts - field appears healthy'
  }

  return {
    total: bySeverity.critical + bySeverity.warning + bySeverity.info,
    bySeverity,
    byType,
    hasActionRequired: bySeverity.critical > 0 || bySeverity.warning > 0,
    primaryConcern: alerts.length > 0 ? alerts[0] : null,
    status,
    statusMessage,
  }
}

// =============================================================================
// AGC: Exports
// =============================================================================

export default {
  // AGC: Cache management
  clearAllMonitoringCache,
  
  // AGC: Observations
  getFieldObservations,
  saveFieldObservation,
  saveFieldObservationsBatch,
  
  // AGC: Stress events
  getStressEvents,
  createStressEvent,
  updateStressEventStatus,
  
  // AGC: Backend API
  fetchFieldHealth,
  fetchTrendAnalysis,
  fetchManagementZones,
  fetchNdviOverlayUrl,
  
  // AGC: Helpers
  generateAlertSummary,
}

